import React from 'react';
import PropTypes from 'prop-types';

import Icon from 'components/icon';
import Link from 'components/link';
import ButtonLink from 'components/button/link';
import ConsoleLink from 'components/console/link';
import KebabButton from 'components/button/kebab';
import ExternalLink from 'components/link/external';
import Status from 'components/status/status';
import AsyncStorefrontStatus from './async-status';
import ClickToCopy from 'components/tooltip/click-to-copy';
import DateTime from 'components/date-time';
import AppIcon from 'components/apps/icon';
import StorefrontIcon from './icon';
import VersionDropdown from './version-dropdown';

import {
  getStorefrontURL,
  renderStorefrontV1ThemeDesc,
} from 'utils/storefront';

import './storefront.scss';

export default class StorefrontItem extends React.PureComponent {
  static contextTypes = {
    user: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
    notifyDeleted: PropTypes.func.isRequired,
  };

  static propTypes = {
    storefronts: PropTypes.object.isRequired,
    getInstalledApps: PropTypes.func.isRequired,
    renderThumbnail: PropTypes.func.isRequired,
    onClickPublish: PropTypes.func.isRequired,
    onClickRename: PropTypes.func.isRequired,
    onClickDuplicate: PropTypes.func.isRequired,
    onClickDelete: PropTypes.func.isRequired,
    detail: PropTypes.bool,
  };

  renderThumbnails(storefront) {
    const { renderThumbnail, detail } = this.props;

    const component = (
      <div className="storefront-thumbnail-container">
        {!detail && storefront.primary ? (
          <>
            <div className="storefront-thumbnail-primary-desktop">
              {renderThumbnail(storefront, 'desktop')}
            </div>
            <div className="storefront-thumbnail-primary-mobile">
              {renderThumbnail(storefront, 'mobile')}
            </div>
          </>
        ) : (
          <>{renderThumbnail(storefront, 'desktop')}</>
        )}
      </div>
    );

    return detail ? (
      component
    ) : (
      <Link to={`/storefronts/${storefront.id}`}>{component}</Link>
    );
  }

  renderLiveUrl(storefront) {
    const { primary, external, app_id } = storefront;

    const storefrontLiveUrl = getStorefrontURL(storefront);
    const hasExternalUrl = Boolean(external?.custom && external?.custom_url);

    return (
      <>
        {storefrontLiveUrl && (primary || hasExternalUrl) ? (
          <div className="storefront-url">
            <ExternalLink to={storefrontLiveUrl} icon={false}>
              {storefrontLiveUrl}
            </ExternalLink>
            <Icon fa="external-link" />
          </div>
        ) : (
          external?.custom &&
          !app_id && (
            <div className="storefront-url">
              <Icon fa="exclamation-circle" />
              <Link to={`/storefronts/${storefront.id}`}>
                Set up your storefront URL.
              </Link>{' '}
              <ExternalLink
                to="https://developers.swell.is/storefronts/headless-storefront-guide"
                icon={false}
              >
                Learn more &rarr;
              </ExternalLink>
            </div>
          )
        )}
      </>
    );
  }

  renderDetails(storefront) {
    const { detail } = this.props;

    return (
      <div className="storefront-details">
        <div className="storefront-title">
          {detail ? (
            storefront.name
          ) : (
            <Link to={`/storefronts/${storefront.id}`}>{storefront.name}</Link>
          )}
        </div>

        {this.renderAppDetails(storefront)}

        {/*this.renderLiveUrl(storefront)*/}

        {this.renderStatus(storefront)}
      </div>
    );
  }

  renderAppDetails(storefront) {
    const {
      app_id,
      app_version,
      theme,
      theme_id,
      theme_app_id,
      theme_version,
      source_model,
      external,
    } = storefront;

    const { storefrontApp, themeApp, deprecatedApp } =
      this.props.getInstalledApps(storefront);

    const themeName =
      themeApp &&
      (theme?.name ||
        themeApp.app?.name ||
        `Theme ${theme_app_id || theme_id}`);

    const appForIcon = themeApp?.app || storefrontApp?.app;
    const appVersion = themeApp
      ? theme_version || theme?.version
      : storefrontApp?.app
      ? app_version || storefrontApp?.app.version
      : app_version || deprecatedApp?.version || deprecatedApp?.app?.version;

    return (
      <div className="storefront-details-app">
        {themeApp ? (
          <>
            {appForIcon && <StorefrontIcon app={appForIcon} size={18} />}
            {this.renderAppVersion(themeName, storefront, themeApp, appVersion)}
          </>
        ) : storefrontApp?.app ? (
          <>
            {appForIcon && <StorefrontIcon app={appForIcon} size={18} />}
            {this.renderAppVersion(
              storefrontApp.app.name || `App ${app_id}`,
              storefront,
              storefrontApp,
              appVersion,
            )}
          </>
        ) : app_id ? (
          <>
            {deprecatedApp?.name || ''} {appVersion}
          </>
        ) : external?.custom ? (
          'Custom'
        ) : (
          source_model && renderStorefrontV1ThemeDesc(storefront)
        )}
        <AsyncStorefrontStatus
          storefront={storefront}
          onCompleted={this.props.fetchAllStorefronts}
        />
      </div>
    );
  }

  renderAppVersion(name, storefront, installedApp, appVersion) {
    return (
      <VersionDropdown
        {...this.props}
        name={name}
        storefront={storefront}
        installedApp={installedApp}
        appVersion={appVersion}
        onSubmitUpdate={this.onSubmitUpdate}
      />
    );
  }

  renderStatus(storefront) {
    const { primary, theme, hosting, external, app_id } = storefront;

    const storefrontLiveUrl = getStorefrontURL(storefront);
    const hasExternalUrl = Boolean(external?.custom && external?.custom_url);
    const lastUpdated =
      theme?.date_updated || storefront.date_updated || storefront.date_created;

    return (
      <div className="storefront-status">
        {primary && theme && (
          <>
            <div className="storefront-status-item">
              <div className="storefront-status-label">Status</div>
              <div className="storefront-status-value">
                <Status type="positive" size="sm" tag={false}>
                  Published
                </Status>
              </div>
            </div>
            {theme.date_published && (
              <div className="storefront-status-item">
                <div className="storefront-status-label">Status</div>
                <div className="storefront-status-value">
                  <DateTime value={theme.date_published} format="ago" />
                </div>
              </div>
            )}
          </>
        )}
        <div className="storefront-status-item">
          <div className="storefront-status-label">Last updated</div>
          <div className="storefront-status-value">
            <DateTime value={lastUpdated} format="ago" />
          </div>
        </div>
        {storefrontLiveUrl && hasExternalUrl ? (
          <div className="storefront-status-item">
            <div className="storefront-status-label">URL</div>
            <div className="storefront-status-value">
              <ExternalLink to={storefrontLiveUrl} icon={false}>
                {storefrontLiveUrl}
              </ExternalLink>
            </div>
          </div>
        ) : (
          external?.custom &&
          !app_id && (
            <div className="storefront-status-item">
              <div className="storefront-status-label">URL</div>
              <div className="storefront-status-value">
                <Icon fa="exclamation-circle" />
                <Link to={`/storefronts/${storefront.id}`}>
                  Set up your storefront URL.
                </Link>{' '}
                <ExternalLink
                  to="https://developers.swell.is/storefronts/headless-storefront-guide"
                  icon={false}
                >
                  Learn more &rarr;
                </ExternalLink>
              </div>
            </div>
          )
        )}
        {hosting?.password_enabled && (
          <div className="storefront-status-item">
            <div className="storefront-status-label">Password</div>
            <div className="storefront-status-value monospaced">
              <ClickToCopy value={hosting.password}>
                {hosting.password} <Icon fa="lock" />
              </ClickToCopy>
            </div>
          </div>
        )}
      </div>
    );
  }

  onSubmitUpdate = async (values) => {
    const { storefront, createStorefront, fetchAllStorefronts } = this.props;

    const created = await createStorefront({
      ...values,
      name: `Updated copy of ${storefront.name.replace(
        /^Updated copy of/,
        '',
      )}`,
      cloned_id: storefront.id,
    });
    if (created && !created?.errors) {
      await fetchAllStorefronts();
    }

    return created;
  };

  renderActions(storefront) {
    const storefrontUrl = getStorefrontURL(storefront, true);
    const { storefrontApp, themeApp } = this.props.getInstalledApps(storefront);

    return (
      <>
        <div className="storefront-actions">
          <KebabButton
            side="bottom"
            disabled={storefront.async_progressing}
            items={[
              !storefront.primary && (
                <button
                  key="publish"
                  onClick={this.props.onClickPublish}
                  data-id={storefront.id}
                >
                  <span className="with-icon">
                    <Icon fa="circle-arrow-up" />
                    Publish
                  </span>
                </button>
              ),
              <ExternalLink key="preview" to={storefrontUrl} icon={false}>
                <span className="with-icon">
                  <Icon fa="external-link" />
                  Preview
                </span>
              </ExternalLink>,
              <Link key="settings" to={`/storefronts/${storefront.id}`}>
                <span className="with-icon">
                  <Icon fa="gear" />
                  Settings
                </span>
              </Link>,
              themeApp && (
                <Link to={`/storefronts/${storefront.id}/code`}>
                  <span className="with-icon">
                    <Icon fa="rectangle-code" />
                    Edit code
                  </span>
                </Link>
              ),
              <hr key="hr-settings" />,
              storefront.primary && (
                <Link key="domains" to={`/storefronts/domains`}>
                  <span className="with-icon">
                    <Icon fa="globe" />
                    Manage domains
                  </span>
                </Link>
              ),
              <Link
                key="navigation"
                to={`/storefronts/${storefront.id}?tab=navigation`}
              >
                <span className="with-icon">
                  <Icon fa="compass" />
                  Manage navigation
                </span>
              </Link>,
              (storefront.app_id || storefront.hosted) && (
                <Link
                  key="password"
                  to={`/storefronts/${storefront.id}#password`}
                >
                  <span className="with-icon">
                    <Icon fa="lock" />
                    Password protection
                  </span>
                </Link>
              ),
              this.renderConsoleLink(storefront),
              <hr key="hr-actions" />,
              <button
                key="rename"
                onClick={this.props.onClickRename}
                data-id={storefront.id}
              >
                Rename
              </button>,
              <button
                key="duplicate"
                onClick={this.props.onClickDuplicate}
                data-id={storefront.id}
              >
                Duplicate
              </button>,
              ...(true /*!storefront.primary*/
                ? [
                    <hr key="hr-delete" />,
                    <button
                      key="remove"
                      className="danger"
                      onClick={this.props.onClickDelete}
                      data-id={storefront.id}
                    >
                      Delete storefront
                    </button>,
                  ]
                : []),
              ...(storefrontApp && themeApp
                ? [
                    <hr key="hr-app" />,
                    <label key="storefront-label">Storefront app</label>,
                    <Link
                      key="storefront-link"
                      to={`/apps/${storefrontApp.app_id}`}
                      className="storefront-dropdown-app-indicator"
                    >
                      <AppIcon app={storefrontApp.app} size={20} />{' '}
                      {storefrontApp.app.name}{' '}
                      {storefrontApp.app_version || storefrontApp.app.version}
                    </Link>,
                  ]
                : []),
            ]}
          />
          {storefront.hosted ? (
            <ButtonLink
              size="sm"
              type="secondary"
              to={`/storefronts/${storefront.id}/v1-editor`}
            >
              Edit theme
            </ButtonLink>
          ) : (
            storefront.theme_id && (
              <ButtonLink
                size="sm"
                type={storefront.primary ? 'default' : 'secondary'}
                to={`/storefronts/${storefront.id}/editor`}
                disabled={storefront.async_progressing}
              >
                Edit theme
              </ButtonLink>
            )
          )}
        </div>
      </>
    );
  }

  renderConsoleLink(storefront) {
    const { user } = this.context;
    if (!user.devtools) {
      return null;
    }

    return (
      <ConsoleLink
        key="console"
        model=":storefronts"
        uri={storefront.id}
        icon={false}
        buttonType={null}
      >
        <span className="with-icon">
          <Icon fa="rectangle-code" />
          View in console
        </span>
      </ConsoleLink>
    );
  }

  render() {
    const { storefront, detail } = this.props;

    return (
      <>
        <div className="storefront-item">
          <div className="storefront-details-container">
            {this.renderThumbnails(storefront)}
            {this.renderDetails(storefront)}
          </div>

          {!detail && this.renderActions(storefront)}
        </div>
      </>
    );
  }
}
